import { BaseService, type ResponseMessage, type SearchRequest } from '@kiddocare-platform/portal-common/base';
import type { AccountDocument, Parent, ParentAddress, Patient, Pediatrician, ProcessName, Setting } from '@kiddocare-platform/portal-common/core';
import type { PediatricianProfileContent, SpecialistSpecialtyGroup } from '@kiddocare-platform/portal-common/core';

export class CoreRegistryService extends BaseService {

  public async CheckAccount(): Promise<ResponseMessage<Setting[]>> {
    try {
      const res = await this.AxiosInstance.get('/parent/registry/accounts/checkAccount');
      return res.data;
    } catch (error: any) {
      this.log.error("CheckAccount", error.stack, JSON.stringify(error));
      throw error
    }
  }

  public async GetParentByAccountId(): Promise<ResponseMessage<Parent>> {
    try {
      const res = await this.AxiosInstance.get('/parent/registry/parents/getParentByAccountId');
      return res.data;
    } catch (error: any) {
      this.log.error("GetParentByAccountId", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async PatientsSearch(query: SearchRequest): Promise<ResponseMessage<Array<Patient>>> {
    try {
      const res = await this.AxiosInstance.post('/parent/registry/patients/search', query);
      return res.data;
    } catch (error: any) {
      this.log.error("PatientsSearch", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async GetPediatricianByPatient(idAccountPatient: bigint): Promise<ResponseMessage<Array<Pediatrician>>> {
    try {
      const res = await this.AxiosInstance.get(`/parent/registry/pediatrician/getPediatricianByPatient/${idAccountPatient}`);
      return res.data;
    } catch (error: any) {
      this.log.error("GetPediatricianByPatient", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async AnagParent(payload: Parent): Promise<ResponseMessage<Parent>> {
    try {
      const res = await this.AxiosInstance.post('/parent/registry/parents/anagParent', payload);
      return res.data;
    } catch (error: any) {
      this.log.error('AnagParent', error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async GetAddress(): Promise<ResponseMessage<Array<ParentAddress>>> {
    try {
      const res = await this.AxiosInstance.get(`/parent/registry/parents/getAddress`);
      return res.data;
    } catch (error: any) {
      this.log.error("GetAddress", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async AnagAddress(data: ParentAddress): Promise<ResponseMessage<unknown>> {
    try {
      const res = await this.AxiosInstance.post(`/parent/registry/parents/anagAddress`, data);
      return res.data;
    } catch (error: any) {
      this.log.error("AnagAddress", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async GetDocument(type: string): Promise<ResponseMessage<AccountDocument>> {
    try {
      const response = await this.AxiosInstance.get(`/parent/registry/accountDocument/getDocument/${type}`);
      return response.data;
    } catch (error: any) {
      this.log.error('GetDocument', error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async GetDocumentPatient(idAccountPatient: bigint, type: string): Promise<ResponseMessage<AccountDocument>> {
    try {
      const response = await this.AxiosInstance.get(`/parent/registry/accountDocument/getDocumentPatient/${idAccountPatient}/${type}`);
      return response.data;
    } catch (error: any) {
      this.log.error('GetDocumentPatient', error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async DeleteAccount(): Promise<ResponseMessage<any>> {
    try {
      const res = await this.AxiosInstance.delete(`/parent/registry/accounts/deleteAccount`)
      return res.data
    } catch (error: any) {
      this.log.error('DeleteAccount', error.stack, JSON.stringify(error))
      throw error
    }
  }

  public async OnboardingConfirmation(): Promise<ResponseMessage<unknown>> {
    try {
      const res = await this.AxiosInstance.put(`/parent/flow/onbordingConfirmation`);
      return res.data;
    } catch (error: any) {
      this.log.error("OnboardingConfirmation", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async CheckFiscalCode(fiscalCode: string): Promise<ResponseMessage<{
    decodedTaxCode: {
      name: string;
      surname: string;
      gender: string;
      day: number;
      month: number;
      year: number;
      birthplace: string;
      idCity?: bigint;
      idCountry?: bigint;
    };
    patient?: Patient;
  }>> {
    try {
      if (!fiscalCode || fiscalCode.length !== 16) {
        throw new Error("InvalidFiscalCode");
      }
      const res = await this.AxiosInstance.get(`/parent/registry/patients/checkFiscalCode/${fiscalCode}`);
      return res.data;
    } catch (error: any) {
      this.log.error("CheckFiscalCode", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async RegisterPatient(payload: {
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string | Date;
    fiscalCode: string;
    sex?: string;
    idCityOfBirth?: number;
    idCountryOfBirth?: number;
    idAsl?: number;
    note?: string;
    idPediatrician?: number;
  }): Promise<ResponseMessage<{
    patient: Patient;
    relationship: string;
  }>> {
    try {
      if (!payload?.fiscalCode || payload.fiscalCode.length !== 16) { throw new Error("FiscalCodeRequired"); }
      const res = await this.AxiosInstance.post(`/parent/registry/patients/registerPatient`, payload
      );
      return res.data;
    } catch (error: any) {
      this.log.error("RegisterPatient", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async UpdatePatient(payload: {
    idPatient: number | bigint;
    firstName?: string;
    lastName?: string;
    fiscalCode?: string;
    dateOfBirth?: string | Date;
    sex?: "M" | "F" | string;
    idCityOfBirth?: number;
    idCountryOfBirth?: number;
    idAsl?: number;
    note?: string;
  }): Promise<ResponseMessage<Patient>> {
    try {
      const res = await this.AxiosInstance.post(`/parent/registry/patients/updatePatient`, payload);
      return res.data;
    } catch (error: any) {
      this.log.error("UpdatePatient", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async DeletePatient(idAccountPatient: bigint): Promise<ResponseMessage<void>> {
    try {
      const res = await this.AxiosInstance.put(`/parent/registry/patients/deletePatient/${idAccountPatient}`);
      return res.data;
    } catch (error: any) {
      this.log.error("DeletePatient", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async AddPediatricianToPatient(data: {
    idAccountPatient: bigint | number | string;
    idAccountPediatrician: bigint | number | string;
  }): Promise<ResponseMessage<unknown>> {
    try {
      const payload = {
        idAccountPatient: String(data.idAccountPatient),
        idAccountPediatrician: String(data.idAccountPediatrician),
      };
      const res = await this.AxiosInstance.post(`/parent/registry/pediatrician/addPediatricianToPatient`, payload);
      return res.data;
    } catch (error: any) {
      this.log.error("AddPediatricianToPatient", error.stack, String(error));
      throw error;
    }
  }

  public async SearchPediatricians(query: SearchRequest): Promise<ResponseMessage<Array<Pediatrician>>> {
    try {
      const res = await this.AxiosInstance.post('/parent/registry/pediatrician/search', query);
      return res.data;
    } catch (error: any) {
      this.log.error("SearchPediatricians", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async DeletePediatricianPatient(data: { idAccountPatient: bigint; idAccountPediatrician: bigint }): Promise<ResponseMessage<void>> {
    try {
      const res = await this.AxiosInstance.post(`/parent/registry/pediatrician/deletePediatricianPatient`, data);
      return res.data;
    } catch (error: any) {
      this.log.error("DeletePediatricianPatient", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  /**
   * Elenco processi/feature disabilitati (es. permessi `*_WRITE` / `*_READ`, `AI`, `BOOKINGS_WRITE`).
   * Se compare `AI`, la modalità consulto AI non è attiva per quel pediatra.
   */
  public async CheckAllDisableProcess(idAccountPediatrician: bigint): Promise<ResponseMessage<Array<string>>> {
    try {
      const res = await this.AxiosInstance.get(`/parent/registry/accountSettings/checkAllDisabledProcess/${idAccountPediatrician}`);
      return res.data;
    } catch (error: any) {
      this.log.error("CheckAllDisableProcess", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async CheckDisableProcess(process: ProcessName, idAccountPediatrician?: bigint): Promise<ResponseMessage<{ code: "ENABLE" | "DISABLE" | "DEACTIVATE", message?: string, mode?: string }>> {
    try {
      const res = await this.AxiosInstance.get(`/parent/registry/accountSettings/checkDisableProcess/${process}/${idAccountPediatrician || ""}`);
      return res.data;
    } catch (error: any) {
      this.log.error("CheckDisableProcess", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async SearchSpecialists(): Promise<ResponseMessage<Array<SpecialistSpecialtyGroup>>> {
    try {
      const res = await this.AxiosInstance.post('/parent/registry/pediatrician/searchSpecialists');
      return res.data;
    } catch (error: any) {
      this.log.error("SearchSpecialists", error.stack, JSON.stringify(error));
      throw error;
    }
  }

  public async GetPediatricianProfile(idAccountPediatrician: bigint): Promise<ResponseMessage<PediatricianProfileContent>> {
    try {
      const res = await this.AxiosInstance.get(`/parent/registry/pediatrician/getPediatricianProfile/${idAccountPediatrician}`);
      return res.data;
    } catch (error: any) {
      this.log.error("GetPediatricianProfile", error.stack, JSON.stringify(error));
      throw error;
    }
  }

}